import React from 'react';
import { useIntl } from 'react-intl';
import { approxFrameSeconds, formatFrameTime, sampleStoryboardFrames, storyboardFrameUrl } from 'src/utils/storyboard';
import StoryboardFrame from './StoryboardFrame';
import messages from './messages';
import styles from './Storyboard.module.scss';

export const STRIP_MAX_FRAMES = 10;
// One requested size for every strip, so the CDN caches each frame once.
export const THUMB_WIDTH = 240;

export type StoryboardSource = {
  assetId: string;
  /** `file_count` of the asset's storyboard conversion */
  count: number;
  /** frame width / height */
  aspect: number;
  /** the video's length in seconds, for approximate frame times */
  durationSeconds?: number;
  /** account storyboard mode; "keyframes" means no times can be given */
  mode?: string;
  jwt?: string;
};

export const thumbUrl = (source: StoryboardSource, frame: number) =>
  storyboardFrameUrl({
    assetId: source.assetId,
    frame,
    width: THUMB_WIDTH,
    height: Math.round(THUMB_WIDTH / source.aspect),
    jwt: source.jwt,
  });

export const useFrameLabel = (source: StoryboardSource) => {
  const { formatMessage } = useIntl();
  return (frame: number) => {
    const seconds = approxFrameSeconds(frame, source.count, source.durationSeconds, source.mode);
    return seconds === null
      ? formatMessage(messages.frameLabel, { frame })
      : formatMessage(messages.frameLabelWithTime, { frame, time: formatFrameTime(seconds) });
  };
};

type StoryboardStripProps = StoryboardSource & {
  /** Open the full viewer, at `frame` when a tile was clicked. */
  onOpenViewer: (frame?: number) => void;
};

/**
 * Up to ten frames, spread across the whole video, for the Formats tab's
 * inline preview. The full set — thousands of frames on long videos — lives
 * in the viewer.
 */
export const StoryboardStrip: React.FC<StoryboardStripProps> = ({ onOpenViewer, ...source }) => {
  const { formatMessage } = useIntl();
  const frameLabel = useFrameLabel(source);
  const frames = sampleStoryboardFrames(source.count, STRIP_MAX_FRAMES);
  const sampled = frames.length < source.count;

  return (
    <div className={styles.strip} data-test="storyboard-strip">
      <div className={styles.stripGrid}>
        {frames.map((frame) => (
          <StoryboardFrame
            key={frame}
            frame={frame}
            src={thumbUrl(source, frame)}
            aspect={source.aspect}
            label={frameLabel(frame)}
            onClick={() => onOpenViewer(frame)}
          />
        ))}
      </div>
      <div className={styles.stripFooter}>
        <span>
          {sampled
            ? formatMessage(messages.stripSummary, { shown: frames.length, count: source.count })
            : formatMessage(messages.stripSummaryAll, { count: source.count })}
        </span>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => onOpenViewer()}
          data-test="storyboard-view-all"
        >
          {sampled
            ? formatMessage(messages.viewAll, { count: source.count })
            : formatMessage(messages.openViewer)}
        </button>
      </div>
    </div>
  );
};

export default StoryboardStrip;
